import { LocalizationKey } from "@/main/util/LocalizationUtil";

/**
 * The one mapping from a `GroupUserBanReasonType` value to its localization key.
 *
 * <p>A group ban stores TWO reason fields: the structured `reasonType` (this enum) and a free-text
 * `reason` the owner types. Only the structured one may be shown to the banned member — the free text
 * is a moderation note written about them, and until 2026-08-19 the banned member's own group page
 * rendered it verbatim, which delivered one owner's unproven fraud allegation to the person it accused
 * (`designs/01-now/2026-08-19-user-safety-phase-0-design.md`). The free text stays behind
 * `assertViewerIsLeader` in the moderation console.
 *
 * <p>This map lived inside `GroupBanUserDialog` until the ban card needed the same labels. Keeping one
 * copy is what makes "the picker and the card name the same reason" true by construction rather than by
 * two lists agreeing; the keys are already translated into every locale because the picker shipped first.
 */
export const GROUP_BAN_REASON_LOCALIZATION_KEYS: Record<string, LocalizationKey> = {
    SPAM: "groups.modal.ban.reason.spam",
    HARASSMENT: "groups.modal.ban.reason.harassment",
    HATE_SPEECH: "groups.modal.ban.reason.hate_speech",
    ABUSE: "groups.modal.ban.reason.abuse",
    RULE_VIOLATION: "groups.modal.ban.reason.rule_violation",
    COPYRIGHT: "groups.modal.ban.reason.copyright",
    AI_SLOP: "groups.modal.ban.reason.ai_slop",
    OTHER: "groups.modal.ban.reason.other",
};

/**
 * The localization key for a reason type, or `undefined` for an absent or unrecognized value.
 *
 * <p>Returns `undefined` rather than falling back to `OTHER` on purpose: an unknown value means the
 * server grew a reason this bundle does not know about, and saying "Other" would be an assertion we
 * cannot support. The caller renders its own neutral "no reason given" copy instead.
 */
export function groupBanReasonLocalizationKey(reasonType: string | null | undefined): LocalizationKey | undefined {
    if (!reasonType) {
        return undefined;
    }
    return GROUP_BAN_REASON_LOCALIZATION_KEYS[reasonType];
}
